import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";
import type { LabResponse, CreateLabRequest, CreateLabResponse, UpdateLabRequest, UpdateLabResponse } from "../types";
import { showNotification } from "@libs/notistack/notificationStack";
import type { AxiosErrorResponse } from "../../../../../types/error.types";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { HTTP_STATUS } from "@constants/http.status.code";
import { useTranslation } from "react-i18next";

export const useGetLabsInfinite = (limit: number = 10, search?: string) => {
	const { t } = useTranslation();
	const normalizedSearch = search?.trim() ?? "";
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.INSTITUTES.LABS, "infinite", normalizedSearch],
		queryFn: async ({ pageParam = 1 }): Promise<LabResponse> => {
			try {
				const url = SERVER_ROUTES.LABS.GET(limit, pageParam, normalizedSearch || undefined);
				const response = await api.get<LabResponse>(url);
				return response.data;
			} catch (error) {
				showNotification({
					message:
						((error as AxiosErrorResponse)?.response?.data?.message as string) ||
						t("feature.courseBuilder.labs.errors.failedToLoadLabs"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
				console.error("Error fetching labs:", error);
				throw error;
			}
		},
		getNextPageParam: lastPage => {
			// The actual API response structure after axios unwrapping is: { data: [...], pagination: {...} }
			// This is different from the LabResponse type which shows the full response structure
			let pagination = null;

			// Try the unwrapped structure first (what we're actually getting)
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			if ((lastPage as any)?.pagination) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				pagination = (lastPage as any).pagination;
			}
			// Fallback to the typed structure
			else if (lastPage?.data?.pagination) {
				pagination = lastPage.data.pagination;
			}

			if (!pagination) {
				return undefined;
			}

			// Use hasNextPage property if available
			if (pagination.hasNextPage !== undefined) {
				const nextPage = pagination.hasNextPage ? pagination.currentPage + 1 : undefined;
				return nextPage;
			}

			// Fallback logic
			const nextPage = pagination.currentPage < pagination.totalPages ? pagination.currentPage + 1 : undefined;
			return nextPage;
		},
		initialPageParam: 1,
		staleTime: 0, // Data is immediately stale
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchOnWindowFocus: false, // Don't refetch when window gains focus
		refetchOnMount: true, // Refetch when component mounts
		retry: 0, //  retry on failure
	});
};

// Delete Lab Mutation
export const useDeleteLab = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (labUuid: string): Promise<{ success: boolean; message: string; data: null }> => {
			const response = await api.delete(SERVER_ROUTES.LABS.DELETE(labUuid));
			return response.data;
		},
		onSuccess: data => {
			showNotification({
				message: (data?.message as string) || t("feature.courseBuilder.labs.success.deleteLab"),
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
			// Force refetch the labs list to ensure UI shows the latest data
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS, "infinite"],
			});
		},
		onError: (error: AxiosErrorResponse) => {
			// Revert optimistic update on error
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS],
			});
			if (error?.response?.data?.statusCode === HTTP_STATUS.CONFLICT) {
				showNotification({
					message:
						(error?.response?.data?.message as string) || t("feature.courseBuilder.labs.errors.failedToDeleteLab"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
			}
		},
	});
};

// Create Lab Mutation
export const useCreateLab = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (labData: CreateLabRequest): Promise<CreateLabResponse> => {
			const response = await api.post<CreateLabResponse>(SERVER_ROUTES.LABS.CREATE, labData);
			showNotification({
				message: (response?.data?.message as string) || t("feature.courseBuilder.labs.success.createLab"),
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
			return response.data;
		},
		onSuccess: () => {
			// Force refetch the labs list to ensure UI shows the latest data
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS, "infinite"],
			});
		},
		onError: (error: AxiosErrorResponse) => {
			// Revert optimistic update on error
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS],
			});
			if (error?.response?.data?.statusCode === HTTP_STATUS.CONFLICT) {
				showNotification({
					message:
						(error?.response?.data?.message as string) || t("feature.courseBuilder.labs.errors.failedToCreateLab"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
			}
		},
	});
};

// Update Lab Mutation
export const useUpdateLabMutation = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async ({
			labUuid,
			labData,
		}: {
			labUuid: string;
			labData: UpdateLabRequest;
		}): Promise<UpdateLabResponse> => {
			const response = await api.patch<UpdateLabResponse>(SERVER_ROUTES.LABS.UPDATE(labUuid), labData);
			return response.data;
		},
		onSuccess: data => {
			// Show success notification
			showNotification({
				message: (data?.message as string) || t("feature.courseBuilder.labs.success.updateLab"),
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});

			// Force refetch the labs list to ensure UI shows the latest data
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS, "infinite"],
			});
		},
		onError: (error: AxiosErrorResponse) => {
			// Revert optimistic update on error
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.INSTITUTES.LABS],
			});
			if (error?.response?.data?.statusCode === HTTP_STATUS.CONFLICT) {
				showNotification({
					message: (error?.response?.data?.message as string) || t("feature.courseBuilder.labs.errors.failedToUpdate"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
			}
		},
	});
};
